
























































































import {
  defineComponent,
  useStatic,
  useContext,
  computed,
  ref,
  watch,
} from '@nuxtjs/composition-api'

export default defineComponent({
  setup(props, ctx) {
    enum Scope {
      TODAY = 'Heute',
      FUTURE = 'Zukünftige',
      PAST = 'Vergangene',
      ALL = 'Alle',
      CUSTOM = 'Benutzerdefiniert',
    }

    const keyword = ref('')
    const keywordDelayTimer = ref(null)
    const filterKeyword = ref('')

    const filterTags = ref([])

    const scopeMenu = ref(false)
    const filterScope = ref(Scope.FUTURE)
    const customDateRange = ref([])

    const scopeValueStr = computed(() => {
      let scope = filterScope.value

      // date range
      if (scope === Scope.CUSTOM) {
        scope =
          customDateRange.value
            .map((d: string) => new Date(d).toLocaleDateString())
            .join(' – ') +
          ` (${filterResultAmount(Scope.CUSTOM, filterTags.value, filterKeyword.value)})`
      }

      return `${scope} (${filterResultAmount(filterScope.value, filterTags.value, filterKeyword.value)})`
    })

    const onKeywordTyping = (str: string) => {
      clearTimeout(keywordDelayTimer.value)
      keywordDelayTimer.value = setTimeout(function () {
        filterKeyword.value = str
      }, 333)
    }

    const closeScopeMenu = (scope: Scope) => {
      if (scope === Scope.CUSTOM && customDateRange.value.length < 2) return // wait for the second date
      filterScope.value = scope
      scopeMenu.value = false
    }

    function filterByScope(veranstaltung: any, scope?: Scope) {
      scope = scope || filterScope.value

      const today = new Date().toISOString().split('T')[0]

      switch (scope) {
        case Scope.CUSTOM:
          return (
            veranstaltung.begin > customDateRange.value[0] &&
            veranstaltung.ende < customDateRange.value[1]
          )

        case Scope.ALL:
          return true

        case Scope.TODAY:
          return veranstaltung.start <= today && veranstaltung.ende >= today

        case Scope.PAST:
          return veranstaltung.ende <= today

        case Scope.FUTURE:
        default:
          return veranstaltung.begin >= today
      }
    }

    function filterByTags(veranstaltung: any, tags?: string[]) {
      tags = tags || filterTags.value

      // has tags to filter with
      if ((tags || []).length > 0) {
        if (Array.isArray(veranstaltung.tags)) {
          return (
            veranstaltung.tags.filter((tag: string) => tags?.includes(tag))
              .length > 0
          )
        }

        return false
      }

      return true
    }

    function filterByKeyword(veranstaltung: any, keyword?: string) {
      keyword = String(keyword || filterKeyword.value).toLowerCase()

      const searchIn = [
        veranstaltung.title,
        veranstaltung.description,
        ...(veranstaltung.tags || []),
      ]

      // has keyword
      if (keyword.length > 0) {
        return searchIn.join(' ').toLowerCase().includes(keyword)
      }

      return true
    }

    function filter(
      veranstaltung: any,
      scope?: Scope,
      tags?: string[],
      keyword?: string,
    ) {
      return (
        filterByScope(veranstaltung, scope) &&
        filterByTags(veranstaltung, tags) &&
        filterByKeyword(veranstaltung, keyword)
      )
    }

    const detailsMaxHeight = computed(() => {
      switch (ctx.root.$vuetify.breakpoint.name) {
        case 'md':
        case 'lg':
        case 'xl':
          return 'max-height: 300px;'
        default:
          return ''
      }
    })

    const textWaitingQueue = (wl: 'männlich' | 'weiblich' | any) => {
      switch (wl) {
        case 'männlich':
          return 'Für Männer nur noch Warteliste'
        case 'weiblich':
          return 'Für Frauen nur noch Warteliste'
        default:
          return 'Nur noch Warteliste'
      }
    }

    const { $content } = useContext()

    const vData = useStatic(
      async () => {
        const veranstaltungen = await $content('veranstaltung')
          .only([
            'slug',
            'title',
            'begin',
            'ende',
            'veranstaltungsort',
            'description',
            'minAlter',
            'maxAlter',
            'featuredImage',
            'warteliste',
            'tags',
            'minTN',
          ])
          .sortBy('begin')
          .fetch()

        return veranstaltungen
      },
      undefined,
      'vDataPage',
    )

    const filterResultAmount = (
      scope: Scope,
      tags: string[],
      keyword: string,
    ) => {
      const filtered = vData.value?.filter((v: any) =>
        filter(v, scope, tags, keyword),
      )

      return (filtered || []).length
    }

    const vDates = computed(() => vData.value.map((v: any) => v.begin))
    const vTags = computed(() => {
      const tags = [
        ...new Set(vData.value.flatMap((v: any) => v.tags || [])),
      ].sort((a: any, b: any) =>
        `${a}`.toLowerCase().localeCompare(`${b}`.toLowerCase()),
      )

      return tags
        .map((tag: any) => {
          const results = filterResultAmount(
            filterScope.value,
            [tag],
            filterKeyword.value,
          )

          return { value: tag, results, text: `${tag} (${results})` }
        })
        .filter((tag: any) => tag.results > 0)
    })

    const veranstaltungen = computed(() =>
      vData.value?.filter((v: any) => filter(v)),
    )

    return {
      detailsMaxHeight,
      textWaitingQueue,
      veranstaltungen,
      vDates,
      filterScope,
      scopeMenu,
      customDateRange,
      scopeValueStr,
      Scope,
      closeScopeMenu,
      filterResultAmount,
      vTags,
      filterTags,
      filterKeyword,
      keyword,
      onKeywordTyping,
    }
  },
  head: {
    title: 'Veranstaltungen',
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: 'Liste aller Veranstaltungen des EC-Nordbundes.',
      },
      // Open Graph
      { hid: 'og:title', property: 'og:title', content: 'Veranstaltungen' },
      {
        hid: 'og:description',
        property: 'og:description',
        content: 'Liste aller Veranstaltungen des EC-Nordbundes.',
      },
      // Twitter Card
      {
        hid: 'twitter:title',
        name: 'twitter:title',
        content: 'Veranstaltungen',
      },
      {
        hid: 'twitter:description',
        name: 'twitter:description',
        content: 'Liste aller Veranstaltungen des EC-Nordbundes..',
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: 'https://www.ec-nordbund.de/veranstaltungen',
        hid: 'canonical',
      },
    ],
  },
})
